import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ChevronLeft, ChevronRight, Search, Star, Users } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { formatCount } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import type * as API from "@shared-core/data/api-types";
import { Input } from "../../primitives/input";
import { Button } from "../../primitives/button";

const PAGE_SIZE = 15;

/**
 * Customer-360's "Contacts" section — every contact Tharstern holds for
 * this customer (not scoped to marketing-imported contacts; see
 * GET /views/contacts), searchable and paginated since a long-standing
 * account can carry hundreds. Replaces the old marketing-contacts-only box:
 * sales reps need to see who's on file at all, with campaign receipt
 * surfaced separately in the Campaign history box above.
 */
interface CustomerContactsProps {
  customerId: string;
  /**
   * Tharstern contact ids currently flagged as the key decision maker
   * (contact-key-decision-maker-flag) — sourced from the customer-360
   * response the parent page already loaded, so this component doesn't need
   * its own fetch to cross-reference it against the rows it renders.
   */
  keyPersonContactIds?: Set<string>;
  /** Called when a rep clicks the toggle on one contact row. */
  onToggleKeyPerson?: (contactId: string, isKeyPerson: boolean) => void;
}

export function CustomerContacts({
  customerId,
  keyPersonContactIds,
  onToggleKeyPerson,
}: CustomerContactsProps) {
  const dataAdapter = useDataAdapter();
  const [contacts, setContacts] = useState<API.ContactViewItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const searchQuery = useDebouncedValue(searchInput, 300);

  // Any filter change restarts pagination from page 1.
  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    dataAdapter
      .getContactViews({
        customerId,
        page,
        pageSize: PAGE_SIZE,
        sort: "full_name",
        ...(searchQuery.trim() ? { q: searchQuery.trim() } : {}),
      })
      .then((result) => {
        if (cancelled) return;
        setContacts(result.items);
        setTotal(result.total);
        setTotalPages(result.totalPages);
      })
      .catch(() => {
        if (cancelled) return;
        setContacts([]);
        setTotal(0);
        setTotalPages(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, customerId, page, searchQuery]);

  return (
    <section
      aria-label="Contacts"
      className="rounded-lg border border-border bg-muted/30 p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <Users className="size-4 text-muted-foreground" aria-hidden />
          <span className="font-medium">Contacts</span>
          {total > 0 && (
            <span className="text-muted-foreground">
              ({formatCount(total)})
            </span>
          )}
        </div>
        <Link
          to={`${paths.contacts()}?customer=${customerId}`}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          View all
          <ChevronRight className="size-3.5" />
        </Link>
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search contacts..."
          className="pl-8 h-8 text-sm"
        />
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading contacts…</p>
      ) : contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {searchQuery.trim()
            ? "No contacts match that search."
            : "No contacts on file for this customer."}
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border bg-background">
          {contacts.map((contact) => {
            const isKeyPerson = keyPersonContactIds?.has(contact.id) ?? false;
            return (
              <li key={contact.id} className="flex items-center gap-2 px-3">
                {onToggleKeyPerson && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onToggleKeyPerson(contact.id, !isKeyPerson);
                    }}
                    aria-pressed={isKeyPerson}
                    aria-label={
                      isKeyPerson
                        ? `Unmark ${contact.name} as key decision maker`
                        : `Mark ${contact.name} as key decision maker`
                    }
                    title={
                      isKeyPerson
                        ? "Key decision maker — click to unflag"
                        : "Mark as key decision maker"
                    }
                    className="shrink-0 text-muted-foreground/60 hover:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  >
                    <Star
                      className={`size-3.5 ${isKeyPerson ? "fill-amber-400 text-amber-500" : ""}`}
                    />
                  </button>
                )}
                <Link
                  to={paths.contactDetail({
                    id: contact.id,
                    customerId: contact.customer_id,
                  })}
                  className="flex flex-1 min-w-0 items-center justify-between gap-3 py-2 text-sm hover:bg-muted/50"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="truncate font-medium text-foreground">
                      {contact.name}
                    </span>
                    {contact.is_primary_customer_contact && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        Primary
                      </span>
                    )}
                    {isKeyPerson && (
                      <span className="text-xs text-amber-600 font-medium shrink-0">
                        Key decision maker
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 text-muted-foreground">
                    {contact.email_address && (
                      <span className="truncate max-w-[14rem]">
                        {contact.email_address}
                      </span>
                    )}
                    {contact.phone_number_1 && (
                      <span className="whitespace-nowrap">
                        {contact.phone_number_1}
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="size-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
